import PropTypes from "prop-types";
import * as React from "react";
import { useDebounce } from "../../../hooks";
import {
  Button,
  Dialog,
  IconButton,
  Switch,
  TextInput,
  DeleteIcon,
} from "../../../themeV2";
import formatDate from "../../../util/formatDate";
import LoadingIndicator from "../../LoadingIndicator";
import UserInvite from "../../UserInvite";
import css from "./AdvancedSettings.module.css";
import { useUserInfluencers, useUserSearch } from "./hooks";
import { addUserAccess, updateUserInfluencer } from "./service";

function AccessListItem({
  user,
  influencerId,
  refresh,
  triggerAlert,
  setErrorText,
}) {
  const [confirmAction, setConfirmAction] = React.useState(null);
  const shopAccessSince = formatDate(user.created);
  const canEdit = !user.no_shop_editor;

  const handleToggleEditor = () => {
    setErrorText("");
    const newValue = canEdit;
    setConfirmAction({
      title: newValue ? "Revoke Editor Access" : "Grant Editor Access",
      message: newValue
        ? `Revoke editor access for ${user.email}?`
        : `Grant editor access to ${user.email}?`,
      action: async () => {
        try {
          await updateUserInfluencer(influencerId, {
            ...user,
            no_shop_editor: newValue,
          });
          refresh();
          triggerAlert({ message: "Saved" });
          setErrorText("");
        } catch (err) {
          console.error(err);
          setErrorText(err.message || "Failed to update access");
        }
        setConfirmAction(null);
      },
    });
  };

  const handleDelete = () => {
    setErrorText("");
    setConfirmAction({
      title: "Remove Access",
      message: `Remove ${user.email}'s access to this shop?`,
      action: async () => {
        try {
          await updateUserInfluencer(influencerId, {
            ...user,
            active: false,
          });
          refresh();
          triggerAlert({ message: "Access removed" });
          setErrorText("");
        } catch (err) {
          console.error(err);
          setErrorText(err.message || "Failed to remove access");
        }
        setConfirmAction(null);
      },
    });
  };

  return (
    <div className={css.accessRow}>
      <div>
        <p
          style={{
            font: "var(--type-body-lg)",
            letterSpacing: "var(--tracking-body-lg)",
            margin: 0,
          }}
        >
          {user.email}
        </p>
        <p className={css.caption} style={{ margin: 0 }}>
          Since {shopAccessSince.date}
        </p>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span className={css.caption}>Can Edit</span>
        <Switch
          checked={canEdit}
          onCheckedChange={handleToggleEditor}
          name={`editor-${user.user_id}`}
        />
        <IconButton
          onClick={handleDelete}
          aria-label={`Remove access for ${user.email}`}
        >
          <DeleteIcon />
        </IconButton>
      </div>

      <Dialog.Root
        alert
        open={Boolean(confirmAction)}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
      >
        <Dialog.Popup size="sm">
          <Dialog.Title>{confirmAction?.title}</Dialog.Title>
          <Dialog.Description>{confirmAction?.message}</Dialog.Description>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "var(--space-2)",
              marginBlockStart: "var(--space-4)",
            }}
          >
            <Button variant="outlined" onClick={() => setConfirmAction(null)}>
              Cancel
            </Button>
            <Button onClick={confirmAction?.action}>Confirm</Button>
          </div>
        </Dialog.Popup>
      </Dialog.Root>
    </div>
  );
}

AccessListItem.propTypes = {
  user: PropTypes.object.isRequired,
  influencerId: PropTypes.number.isRequired,
  refresh: PropTypes.func.isRequired,
  triggerAlert: PropTypes.func.isRequired,
  setErrorText: PropTypes.func.isRequired,
};

export default function AccessList({
  influencer,
  triggerAlert,
  setErrorText,
  userInviteModalAtom,
}) {
  const { userInfluencers, isLoading, refresh } = useUserInfluencers(
    influencer.influencer_id,
  );
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const [isAdding, setIsAdding] = React.useState(false);

  useDebounce(() => setDebouncedQuery(searchQuery), 500, [searchQuery]);

  const { users, isLoading: isSearching } = useUserSearch(debouncedQuery);

  const handleAddUser = async (userId) => {
    if (isAdding) return;
    setIsAdding(true);
    setErrorText("");

    try {
      await addUserAccess(influencer.influencer_id, userId);
      refresh();
      triggerAlert({ message: "User added" });
      setErrorText("");
      setSearchOpen(false);
      setSearchQuery("");
      setDebouncedQuery("");
    } catch (err) {
      console.error(err);
      setErrorText(err.message || "Failed to add user");
    } finally {
      setIsAdding(false);
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          position: "relative",
          height: 30,
          width: 30,
          marginInline: "auto",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div>
      <p className={css.caption}>
        {userInfluencers.length} user{userInfluencers.length !== 1 ? "s" : ""}{" "}
        have access
      </p>

      {userInfluencers.length > 0 ? (
        <div>
          {userInfluencers.map((user) => (
            <AccessListItem
              key={user.user_influencer_id}
              user={user}
              influencerId={influencer.influencer_id}
              refresh={refresh}
              triggerAlert={triggerAlert}
              setErrorText={setErrorText}
            />
          ))}
        </div>
      ) : (
        <p className={css.caption}>No users have access yet</p>
      )}

      <div
        style={{
          display: "flex",
          gap: "var(--space-2)",
          marginBlockStart: "var(--space-2)",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            setErrorText("");
            setSearchOpen((prev) => !prev);
          }}
        >
          {searchOpen ? "Cancel" : "Add Existing User"}
        </Button>

        <UserInvite
          influencerId={influencer.influencer_id}
          stateAtom={userInviteModalAtom}
          color="default"
          variant="outlined"
          size="small"
          label="Invite New User"
        />
      </div>

      {searchOpen && (
        <div className={css.searchPanel}>
          <TextInput
            label="Search by email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            description="Type at least 4 characters to search"
            autoFocus
          />

          {isSearching && <p className={css.caption}>Searching...</p>}

          {users.length > 0 && (
            <ul className={css.resultList}>
              {users.map((user) => (
                <li
                  key={user.user_id}
                  className={css.resultItem}
                  role="button"
                  tabIndex={0}
                  aria-disabled={isAdding}
                  onClick={() => handleAddUser(user.user_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleAddUser(user.user_id);
                    }
                  }}
                >
                  {user.email}
                </li>
              ))}
            </ul>
          )}

          {debouncedQuery.length >= 4 && !isSearching && users.length === 0 && (
            <p className={css.caption}>
              No users found. Use &ldquo;Invite User&rdquo; above to add them to
              the platform first.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

AccessList.propTypes = {
  influencer: PropTypes.shape({
    influencer_id: PropTypes.number.isRequired,
  }),
  triggerAlert: PropTypes.func.isRequired,
  setErrorText: PropTypes.func.isRequired,
  userInviteModalAtom: PropTypes.object.isRequired,
};
